import React from 'react';
import { StarIcon } from 'lucide-react';
import { ResolvedRequirement, Role } from '../types/commission';
import { RequirementRow } from './RequirementRow';
interface TierCardProps {
  role: Role;
  tiers: Role[];
  requirements: ResolvedRequirement[];
  onOpenRequirement: (label: string) => void;
}

/** Current production tier with progress toward the next tier. */
export function TierCard({
  role,
  tiers,
  requirements,
  onOpenRequirement
}: TierCardProps) {
  const tierIndex = Math.max(0, tiers.findIndex(tier => tier.id === role.id));
  const nextTier = tiers[tierIndex + 1];
  const promotion = role.promotion;
  const bpsDelta = nextTier ? (nextTier.baseBps ?? 0) - (role.baseBps ?? 0) : 0;
  return <article aria-labelledby="tier-heading" className="flex h-full flex-col overflow-hidden rounded-xl border border-brand-900 bg-brand-900 p-6 shadow-card" data-id="element-515" data-mp="17o6ms2b2kci6">
      <div className="flex items-start justify-between gap-4" data-id="element-516" data-mp="39f09randzw07">
        <div data-id="element-517" data-mp="39gkzmmyraqrq">
          <p className="text-xs font-semibold uppercase tracking-widest text-brand-200" data-id="element-518" data-mp="3lqyamj2iobx6">Current tier</p>
          <h2 id="tier-heading" className="mt-1.5 text-2xl font-bold text-white" data-id="element-519" data-mp="3qp85eskbxai9">
            {role.name}
          </h2>
        </div>
        <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/25" data-id="element-520" data-mp="3vo2n1r6k8t0s">
          <StarIcon className="h-5 w-5 fill-gold-400 text-gold-400" data-id="element-521" data-mp="9a1d3eeeiydb" />
        </span>
      </div>

      <div className="mt-5 flex gap-1.5" aria-hidden="true" data-id="element-522" data-mp="dabrnfg9ex3s">
        {tiers.map((tier, index) => <span key={tier.id} className={`h-1.5 flex-1 rounded-full ${index <= tierIndex ? 'bg-gold-400' : 'bg-white/20'}`} data-id="element-523" data-mp="i9og24ms4c8h" data-mpi={index} />)}
      </div>
      <p className="mt-2 text-xs font-medium text-brand-200" data-id="element-524" data-mp="2k1kp97lh5a03">
        Tier {tierIndex + 1} of {tiers.length} · {role.baseBps} BPS base
      </p>

      {promotion ? <div className="mt-auto space-y-4 pt-6" data-id="element-525" data-mp="2p2i7fk6hi41l">
          <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-white" data-id="element-526" data-mp="2gyy4rompq1ex">
            Next: {promotion.toRoleName}
            {nextTier ? <span className="rounded-full bg-gold-400/20 px-2 py-0.5 text-xs font-bold text-gold-300" data-id="element-527" data-mp="2d0h7rxx0a7b8">
                {nextTier.baseBps} BPS base{bpsDelta > 0 ? ` · +${bpsDelta}` : ''}
              </span> : null}
            {requirements.length > 1 ? <span className="text-xs font-medium uppercase tracking-wide text-brand-300" data-id="element-528" data-mp="2zwr7aptfc9bs">
                {promotion.logic === 'or' ? 'Either path' : 'Both required'}
              </span> : null}
          </p>
          {requirements.map(requirement => <RequirementRow key={requirement.label} requirement={requirement} onOpen={onOpenRequirement} data-id="element-529" data-mp="34qbvxr94nqyb" />)}
        </div> : null}
    </article>;
}